import { el, clear } from '../../utils/dom.js';
import { Reservations, Loans, Rentals } from '../../api/resources.js';
import { statusBadgeClass } from '../../utils/format.js';
import { err } from '../../components/toast.js';

const DAY_NAMES = ['Lun','Mar','Mie','Jue','Vie','Sab','Dom'];
const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export default async function ReservationsCalendarPage(outlet, ctx) {
  clear(outlet);

  const today = new Date();
  let viewYear  = parseInt(ctx.query.year  || today.getFullYear(), 10);
  let viewMonth = parseInt(ctx.query.month || (today.getMonth() + 1), 10) - 1; // 0-11

  let showLoans   = ctx.query.loans !== 'false';
  let showRentals = ctx.query.rentals !== 'false';
  let showRes     = ctx.query.reservations !== 'false';

  outlet.appendChild(el('div', { class: 'page-head' },
    el('h1', {}, 'Calendario'),
    el('div', { class: 'flex gap-2' },
      el('a', { href: '#/reservations', class: 'btn ghost' }, '← Lista'),
    ),
  ));

  // Toolbar: navegacion + filtros
  const monthLabel = el('div', { class: 'text-bold', style: 'min-width: 200px; text-align:center; font-size: 16px;' });

  function goPrev() { viewMonth--; if (viewMonth < 0) { viewMonth = 11; viewYear--; } render(); }
  function goNext() { viewMonth++; if (viewMonth > 11) { viewMonth = 0;  viewYear++; } render(); }
  function goToday() { viewMonth = today.getMonth(); viewYear = today.getFullYear(); render(); }

  const toolbar = el('div', { class: 'toolbar' },
    el('button', { class: 'btn', onclick: goPrev },  '←'),
    monthLabel,
    el('button', { class: 'btn', onclick: goNext },  '→'),
    el('button', { class: 'btn', onclick: goToday }, 'Hoy'),
    el('div', { class: 'flex-1' }),
    toggle('Reservas', () => showRes,     (v) => { showRes = v;     render(); }, 'res'),
    toggle('Prestamos',() => showLoans,   (v) => { showLoans = v;   render(); }, 'loan'),
    toggle('Rentas',   () => showRentals, (v) => { showRentals = v; render(); }, 'rent'),
  );
  outlet.appendChild(toolbar);

  function toggle(label, getValue, setValue, kind) {
    const id = `t_${kind}_${Math.random().toString(36).slice(2,6)}`;
    const cb = el('input', { type: 'checkbox', id, checked: getValue(), onchange: (e) => setValue(e.target.checked) });
    return el('label', { for: id, class: 'checkbox' }, cb, el('span', { class: `badge ${chipFor(kind)}` }, label));
  }

  const grid = el('div', { class: 'card', style: 'padding: 0; overflow: hidden;' });
  outlet.appendChild(grid);

  const legend = el('div', { class: 'flex gap-3 mt-3 muted text-sm wrap' },
    legendDot('Reserva pendiente',   'reservation pendiente'),
    legendDot('Reserva confirmada',  'reservation confirmada'),
    legendDot('Prestamo activo',     'loan'),
    legendDot('Renta activa',        'rental'),
    legendDot('Vencido / vencida',   'overdue'),
  );
  outlet.appendChild(legend);

  function legendDot(label, type) {
    return el('span', { class: 'flex items-center gap-2' },
      el('span', { class: `cal-dot ${type}` }),
      label,
    );
  }

  await render();

  async function render() {
    monthLabel.textContent = `${MONTH_NAMES[viewMonth]} ${viewYear}`;
    grid.innerHTML = '<div class="empty">Cargando...</div>';

    // Rango del mes (extendido a la semana visible)
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const lastOfMonth  = new Date(viewYear, viewMonth + 1, 0);
    const startOffset = (firstOfMonth.getDay() + 6) % 7; // lunes=0
    const gridStart = new Date(viewYear, viewMonth, 1 - startOffset);
    const totalDays = startOffset + lastOfMonth.getDate();
    const numWeeks = Math.ceil(totalDays / 7);
    const gridEnd = new Date(gridStart);
    gridEnd.setDate(gridStart.getDate() + numWeeks * 7);

    let events = [];
    try {
      const [resData, loanData, rentalData] = await Promise.all([
        showRes     ? Reservations.list({ from_date: gridStart.toISOString(), to_date: gridEnd.toISOString(), limit: 200 }).catch(() => ({ data: [] })) : { data: [] },
        showLoans   ? Loans.list({ from_date: gridStart.toISOString(), to_date: gridEnd.toISOString(), limit: 200 }).catch(() => ({ data: [] })) : { data: [] },
        showRentals ? Rentals.list({ from_date: gridStart.toISOString(), to_date: gridEnd.toISOString(), limit: 200 }).catch(() => ({ data: [] })) : { data: [] },
      ]);
      events = [
        ...(resData.data || []).map((r) => ({
          source: 'reservation', id: r.id, code: r.code, status: r.status,
          start: r.start_date, end: r.end_date,
          who: r.client_name || r.responsible_name || '—',
          link: `/reservations/${r.id}`,
        })),
        ...(loanData.data || []).map((r) => ({
          source: 'loan', id: r.id, code: r.code, status: r.status,
          start: r.start_date, end: r.expected_return_date,
          who: r.responsible_name || '—',
          link: `/loans/${r.id}`,
        })),
        ...(rentalData.data || []).map((r) => ({
          source: 'rental', id: r.id, code: r.code, status: r.status,
          start: r.start_date, end: r.end_date,
          who: r.client_name || '—',
          link: `/rentals/${r.id}`,
        })),
      ];
    } catch (ex) {
      grid.innerHTML = `<div class="empty">Error: ${ex.message}</div>`;
      err(ex.message);
      return;
    }

    grid.replaceChildren();

    // Header de dias
    const head = el('div', { class: 'cal-head' });
    for (const d of DAY_NAMES) head.appendChild(el('div', {}, d));
    grid.appendChild(head);

    // Cuerpo
    const body = el('div', { class: 'cal-body', style: `grid-template-rows: repeat(${numWeeks}, minmax(108px, 1fr));` });
    for (let i = 0; i < numWeeks * 7; i++) {
      const day = new Date(gridStart);
      day.setDate(gridStart.getDate() + i);
      const inMonth = day.getMonth() === viewMonth;
      const isToday = sameDay(day, today);

      const cell = el('div', { class: `cal-cell ${inMonth ? '' : 'out'} ${isToday ? 'today' : ''}` },
        el('div', { class: 'cal-num' }, String(day.getDate())),
      );

      // Eventos que cubren este dia
      const dayEvents = events.filter((e) => coversDay(e, day)).slice(0, 4);
      for (const ev of dayEvents) {
        const isStart = sameDay(new Date(ev.start), day);
        const isEnd   = sameDay(new Date(ev.end), day);
        const overdue = ['vencido','vencida'].includes(ev.status);
        const chipKind = overdue ? 'overdue' :
          ev.source === 'reservation' ? `reservation ${ev.status}` :
          ev.source === 'loan' ? 'loan' : 'rental';
        cell.appendChild(el('a', {
          class: `cal-evt ${chipKind}`,
          href: '#' + ev.link,
          title: `${ev.code} · ${ev.who} · ${ev.status}`,
        },
          el('span', { class: 'cal-evt-dot' }),
          el('span', { class: 'truncate' },
            isStart ? `${ev.code} ${ev.who}` :
            isEnd   ? `→ ${ev.code}` :
            `· ${ev.code}`,
          ),
        ));
      }

      const more = events.filter((e) => coversDay(e, day)).length - dayEvents.length;
      if (more > 0) {
        cell.appendChild(el('div', { class: 'cal-more' }, `+${more} mas`));
      }
      body.appendChild(cell);
    }
    grid.appendChild(body);
  }
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth()    === b.getMonth() &&
         a.getDate()     === b.getDate();
}

function coversDay(ev, day) {
  const start = new Date(ev.start); start.setHours(0,0,0,0);
  const end   = new Date(ev.end);   end.setHours(23,59,59,999);
  const d = new Date(day);          d.setHours(12,0,0,0);
  return d >= start && d <= end;
}

function chipFor(kind) {
  if (kind === 'res')  return 'info';
  if (kind === 'loan') return 'warn';
  if (kind === 'rent') return 'primary';
  return 'muted';
}
